import { BudgetScenarioAttributes, BudgetScenarioType } from '@callmart/types';
import api from './index';

const BASE = '/budget-scenarios';

export interface BudgetScenario extends BudgetScenarioAttributes {}

export interface BudgetScenarioPayload {
    name: string;
    description?: string | null;
    type: BudgetScenarioType;
    budgetVersionId: number;
    startDate: string; // 'YYYY-MM-DD'
    endDate: string; // 'YYYY-MM-DD'
    // Duplicate: copy this scenario's type-specific inputs into the new one. The server takes the
    // source's type and requires the target version to be in the source's bucket.
    cloneFromId?: number;
}

export interface ScenarioContactsInputEntity {
    worktypeId: number;
    // CALL-783 Stage 4: the worktype's training block travels ON the entity, exactly as the shift and
    // driver rows carry theirs — one row is one saveable unit, so the deltas and the training that goes
    // with them cannot arrive in two halves.
    agents: number;
    trainingDays: number;
    costPerHour: number;
    trainerCost: number;
    /** The training allocation window (CALL-801), like the shift row's — but null trainingStart is a
     *  valid saved state here: the backend then walks from the scenario start. */
    trainingStart: string | null;
    trainingEnd: string | null;
    values: Array<{ month: string; contactsDelta: number }>;
}

export interface ScenarioShiftInputRow {
    // CALL-783: a Verschiebung moves volume WITHIN one working unit (worktype – channel – task) from one
    // location to another, so the row is keyed by working unit rather than by worktype and the field is
    // required — the board's table always names one.
    workingUnitId: number;
    fromLocationId: number;
    toLocationId: number;
    agents: number;
    /** Training days per agent — what the training total is computed FROM, not the length of the window
     *  below. `share` used to sit here and is gone with CALL-783: no calc ever read it. */
    trainingDays: number;
    costPerHour: number;
    trainerCost: number;
    trainingStart: string | null; // 'YYYY-MM-DD' or null
    /** The entered end of the training window (CALL-783); null = the backend walks `trainingDays`
     *  weekdays from the start. It decides which months the training cost is booked into. */
    trainingEnd: string | null;
    ramp: Array<{ month: string; sharePct: number }>;
}

export interface ScenarioDriverInputRow {
    worktypeId: number;
    wlPer1000: number;
    cpc: number;
    headsTraining: number;
    trainingDays: number;
    costPerHour: number;
    trainerResource: number;
    /** The training allocation window (CALL-801) — see ScenarioContactsInputEntity. */
    trainingStart: string | null;
    trainingEnd: string | null;
    values: Array<{ month: string; driverDelta: number }>;
}

/** The active type's whole input set, carried by the scenario PATCH (CALL-783).
 *
 *  The payload IS the desired final state — rows absent from it are deleted server-side — so no row
 *  carries an id. `month` is always a first-of-month 'YYYY-MM-DD'. */
export type ScenarioInputsPayload =
    | { type: BudgetScenarioType.CONTACTS; entities: ScenarioContactsInputEntity[] }
    | {
          type: BudgetScenarioType.MINUTE_PRICE;
          entities: Array<{ locationId: number; values: Array<{ month: string; monthlyValue: number }> }>;
      }
    | { type: BudgetScenarioType.SHIFT; rows: ScenarioShiftInputRow[] }
    | {
          type: BudgetScenarioType.BUDGET_ADJUSTMENT;
          values: Array<{ month: string; amount: number }>;
          worktypeIds: number[];
      }
    | { type: BudgetScenarioType.REACHABILITY_ADJUSTMENT; rows: ScenarioDriverInputRow[] };

/** Metadata plus, optionally, the whole input set of the scenario's type in ONE request (CALL-783).
 *  `revision` is the `updatedAt` the draft was based on; a stale one is answered with a 409 rather
 *  than silently overwriting another session's inputs. It is required whenever `inputs` is sent. */
export interface BudgetScenarioUpdatePayload extends Partial<BudgetScenarioPayload> {
    revision?: string;
    inputs?: ScenarioInputsPayload;
}

/** `bucketId` omitted = every bucket (the overview's list). Axios drops an undefined param, so the
 *  unscoped call is the same request it always was. */
export const getBudgetScenarios = async (bucketId?: number): Promise<BudgetScenario[]> => {
    return api.get(BASE, { params: { bucketId } }).then((res) => res.data);
};

export const getBudgetScenario = async (id: number): Promise<BudgetScenario> => {
    return api.get(`${BASE}/${id}`).then((res) => res.data);
};

export const createBudgetScenario = async (data: BudgetScenarioPayload): Promise<BudgetScenario> => {
    return api.post(BASE, data).then((res) => res.data);
};

export const updateBudgetScenario = async ({
    id,
    ...data
}: { id: number } & BudgetScenarioUpdatePayload): Promise<BudgetScenario> => {
    return api.patch(`${BASE}/${id}`, data).then((res) => res.data);
};

export const deleteBudgetScenario = async (id: number) => {
    return api.delete(`${BASE}/${id}`).then((res) => res.data);
};
